import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, CircularProgress, Typography, MenuItem, Grid, Chip
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { getBranchUsers, createBranchStaff, resetBranchUserPassword } from '../../services/api';
import { isAdmin, getUserRole } from '../../utils/auth';

const BranchStaff = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState({ username: '', full_name: '', email: '', password: '', staff_role: 'branch_staff' });
  const [passwordForm, setPasswordForm] = useState({ password: '' });
  const userRole = getUserRole();
  const allowManage = isAdmin() || userRole === 'branch_manager';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getBranchUsers();
      setUsers((res.data || []).filter((u) => u.role !== 'branch_manager' && u.role !== 'admin'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await createBranchStaff(form);
      setDialogOpen(false);
      setForm({ username: '', full_name: '', email: '', password: '', staff_role: 'branch_staff' });
      setSuccess('Staff user created');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async () => {
    try {
      await resetBranchUserPassword(selectedUser.id, passwordForm);
      setPasswordDialog(false);
      setPasswordForm({ password: '' });
      setSuccess('Password updated');
    } catch (err) {
      setError(err.message);
    }
  };

  if (!allowManage) {
    return <Alert severity="warning">Only super admin, admin, or branch managers can manage branch staff.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>👥 Branch Staff</Typography>
          <Typography color="text.secondary">Create and manage staff for your branch</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>Add Staff</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell><strong>{u.username}</strong></TableCell>
                  <TableCell>{u.full_name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell><Chip label={u.role} size="small" /></TableCell>
                  <TableCell><Chip label={u.is_active !== false ? 'Active' : 'Inactive'} size="small" color="success" /></TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => { setSelectedUser(u); setPasswordDialog(true); }}>Reset Password</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Branch Staff</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}><TextField fullWidth label="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Full Name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Grid>
            {(isAdmin() || userRole === 'branch_manager') && (
              <Grid item xs={12}>
                <TextField select fullWidth label="Staff Role" value={form.staff_role} onChange={(e) => setForm({ ...form, staff_role: e.target.value })}>
                  <MenuItem value="branch_staff">Branch Staff</MenuItem>
                  <MenuItem value="technician">Technician</MenuItem>
                  {isAdmin() && <MenuItem value="cashier">Cashier</MenuItem>}
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="New Password" type="password" value={passwordForm.password} onChange={(e) => setPasswordForm({ password: e.target.value })} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword}>Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchStaff;
